import { createContext, useContext } from 'react'

/** Was eine Liste der Ebene meldet: ihre Zeilen und was davon in der Hülle liegt. */
export interface HullSource {
  /** Ein Element direkt in der Liste; gesucht wird in dessen Elternteil. */
  el: HTMLElement | null
  rowIds: readonly string[]
  /** Die markierten Zeilen ohne die aufgeklappte. */
  inIds: ReadonlySet<string>
  /** Die aufgeklappte Zeile — geht sie auf oder zu, steht die Fläche sofort, statt zu fahren. */
  excludedId: string | null
  dormantIds?: ReadonlySet<string>
  /** Das Attribut, an dem die Zeilen im Elternteil gefunden werden. Entfällt mit `measureSelf`. */
  rowAttribute?: string
  rowSelector?: string
  /** Auswahl samt Ruhe-Marke als Text — ändert er sich, fährt die Fläche. */
  selectionKey: string
  suspended: boolean
  /**
   * Die Quelle ist selbst eine Zeile (ein Abschnittskopf, Kennung `rowIds[0]`) und misst sich selbst,
   * statt im Elternteil gesucht zu werden — etwa an ihrem Platz im Fluss, während sie angeheftet ist.
   */
  measureSelf?: () => DOMRect | null
}

/**
 * Die Meldungen aller Listen einer Ebene. Gemeldet wird in den Layout-Effekten der Listen; die Ebene
 * erfährt davon über `subscribe` und rechnet einmal mit allen — sähe sie das Abwählen in Liste A,
 * bevor Liste B ihre neue Zeile meldet, blendete sie aus statt zu wandern.
 */
export class HullStore {
  readonly sources = new Map<string, HullSource>()
  private listener: (() => void) | null = null

  subscribe(listener: () => void): () => void {
    this.listener = listener
    if (this.sources.size > 0) listener()
    return () => {
      if (this.listener === listener) this.listener = null
    }
  }

  /** Meldet nur weiter, was die Fläche ändert — eine neue Menge mit gleichem Inhalt nicht. */
  set(id: string, source: HullSource): void {
    const prev = this.sources.get(id)
    this.sources.set(id, source)
    if (
      !prev
      || prev.selectionKey !== source.selectionKey
      || prev.suspended !== source.suspended
      // Die offene Karte wechselt (Tab), ohne dass sich der Schlüssel ändert — die Ebene muss es
      // trotzdem wissen, sonst gilt das Zuklappen danach als neue Auswahl und blendet ein.
      || prev.excludedId !== source.excludedId
      || prev.rowIds !== source.rowIds
      || prev.el !== source.el
    ) this.listener?.()
  }

  delete(id: string): void {
    if (this.sources.delete(id)) this.listener?.()
  }
}

export interface HullScope {
  store: HullStore
  /** Die ganze Seite ruht (ein Abschnitt wird gezogen): keine Fläche, die Zeilen malen sich selbst. */
  resting: boolean
}

export const HullScopeContext = createContext<HullScope | null>(null)

/**
 * Ruht die Hülle der Seite? Dann malen die Zeilen ihre Markierung selbst.
 *
 * @example
 * const scopeResting = useSelectionHullResting()
 * <TaskEntry selectionHull={!scopeResting} … />
 */
export function useSelectionHullResting(): boolean {
  return useContext(HullScopeContext)?.resting ?? false
}
